/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ChevronDown } from 'lucide-react';
import LinkButton from '|/components/carvana-ds/link-button';
import cx from 'classnames';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { StepName } from '|/app/services';
import GapCoverageModal from '|/components/carvana-ds/order-summary-display/gap-coverage-modal';
import SlateAccessoriesModal from '|/components/carvana-ds/slate-accessories-modal';
import { useSlate } from '|/context';
import { getGapModalProps } from '|/helpers/carvana-care-modal-variants';
import currency from '|/helpers/currency';
import { getDealerFeeData, getDealerFeeAmount } from '|/helpers/dealer-fees';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { ACCESSORIES_SHIPPING_COST } from '|/services/slate-accessories';
import DiscountsBreakdown from './discounts-breakdown';
import styles from './price-breakdown.module.css';
import useOrderSummaryVm from '../../use-order-summary-vm';
import useSlateAccessories from '../../use-slate-accessories';

interface PriceBreakdownProps {
  summary: ReviewSummaryResponse;
  vinEtchingStatus?: null | string;
  setIsVinEtchingModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTradeInModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCarvanaCareModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  testId?: string;
  setIsDealerFeesModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  isInNoDealerFeesExperiment?: boolean | null;
  isInTreatment?: boolean;
  isExperimentLoading?: boolean;
  experimentName?: string;
  experimentArm?: string | null;
  isFinanced?: boolean;
}

const PriceBreakdown = ({
  summary,
  vinEtchingStatus,
  setIsVinEtchingModalOpen = () => {},
  setIsTradeInModalOpen = () => {},
  setIsCarvanaCareModalOpen = () => {},
  testId = 'price-breakdown-section',
  setIsDealerFeesModalOpen = () => {},
  isInNoDealerFeesExperiment = false,
  isInTreatment = false,
  isExperimentLoading = false,
  experimentName,
  experimentArm,
  isFinanced = true
}: PriceBreakdownProps) => {
  const { trackEvent } = useAnalyticsContext();
  const navigateToStep = useNavigateToStep();
  const {
    collapsedFees,
    setCollapsedFees,
    tradeDeclined,
    completedTrade,
    calculatedTradeValue,
    tradeMessage,
    totalTaxes,
    totalFeesAmount: totalFees,
    totalDiscountsAmount: totalDiscounts,
    tradeBoostDiscount,
    creditKarmaDiscount,
    tradeInTaxSavings,
    subtotal,
    shippingFee,
    deliveryCharge,
    gapStatus,
    gapPrice,
    productStatus,
    productPrice,
    productType,
    productMonths,
    productMileage,
    hasTransferMethod,
    isPickup,
    isDelivery,
    isVendingMachine,
    transferAddress,
    totalPrice,
    creditFromPreviousOrder,
    vehicleStickerPrice,
    feesList,
    hasEvTaxCredit,
    isEvTaxCreditEligible,
    evTaxCreditAmount,
    creditKarmaAmountSaved,
    tradeBoostAmountSaved,
    employeeDiscount,
    employeeDiscountAmount,
    isTradeInEligible,
    msrp,
    carvanaDiscount,
    incentives,
    collapsedIncentives,
    setCollapsedIncentives,
    totalIncentives,
    collapsedShipping,
    setCollapsedShipping,
    isCash,
    tradeBreakdownTitle
  } = useOrderSummaryVm(summary);
  const { vinEtching: { isEligible: showVinEtching = false } = {} } = summary || {};
  const isVinEtchingApplied = vinEtchingStatus?.toLowerCase() === 'applied';
  const mayShowVinEtching = vinEtchingStatus !== null;
  const isCashPaymentWithDifferential = isCash && (creditFromPreviousOrder || 0) > 0;

  // Determine if any selections should be shown to avoid empty spacing
  const hasSelections =
    (isTradeInEligible && completedTrade) ||
    (hasTransferMethod && transferAddress) ||
    (productStatus !== null && productStatus !== 'NotSet') ||
    (gapStatus !== null && gapStatus !== 'NotSet') ||
    isEvTaxCreditEligible ||
    (mayShowVinEtching && showVinEtching) ||
    isCashPaymentWithDifferential;

  const negative = totalIncentives !== null && totalIncentives > 0 ? '-' : '';

  const userState = summary?.customer?.address?.state ?? '';

  const stateData = getDealerFeeData(userState);

  const feeAmount = stateData ? getDealerFeeAmount(stateData) : 0;
  const stateName = stateData?.state ?? 'your state';
  const dealerFeeSavingsText = `No dealer fees means you save up to $${feeAmount} in ${stateName}.`;
  const isNewCar = msrp !== null && msrp > 0;

  const hasDiscounts =
    totalDiscounts > 0 || tradeBoostDiscount || creditKarmaDiscount || employeeDiscount;

  const [isGapModalOpen, setIsGapModalOpen] = useState(false);
  const gapModalProps = getGapModalProps(summary);

  const { isSlateVehicle } = useSlate();
  const { accessories, accessoriesTotal } = useSlateAccessories(isSlateVehicle);
  const showAccessories = isSlateVehicle && accessories.length > 0;
  const [isAccessoriesModalOpen, setIsAccessoriesModalOpen] = useState(false);

  return (
    <section data-testid={testId}>
      <div className={styles.wrapper}>
        <div className={styles.breakdownSection}>
          <h3>Price breakdown</h3>

          {(hasDiscounts || isNewCar || showAccessories) && (
            <div className={styles.breakdownList}>
              {isNewCar ? (
                <>
                  <div className={styles.itemRow}>
                    <p className={styles.subItem}>MSRP</p>
                    <p>{currency(msrp)}</p>
                  </div>

                  <div className={cx(styles.itemRow, styles.greenText)}>
                    <p className={styles.subItem}>Customer discount</p>
                    <p className={styles.discountAmount}>-{currency(carvanaDiscount ?? 0)}</p>
                  </div>
                </>
              ) : (
                <div className={styles.itemRow}>
                  <p className={styles.subItem}>List price</p>
                  <p>{currency(vehicleStickerPrice ?? 0)}</p>
                </div>
              )}

              <DiscountsBreakdown summary={summary} />

              {showAccessories && (
                <>
                  <div className={styles.itemRow}>
                    <p className={styles.subItem}>Accessories</p>
                  </div>
                  <div className={styles.dropdownContent}>
                    {accessories.map(accessory => (
                      <React.Fragment key={accessory.id}>
                        <div className={styles.itemRow}>
                          <p>{accessory.name}</p>
                          <p className={styles.strongAmount}>{currency(accessory.price)}</p>
                        </div>
                        {accessory.installation?.factoryInstall && (
                          <div className={styles.itemRow}>
                            <p>{accessory.name} installation</p>
                            <p className={styles.strongAmount}>
                              {currency(accessory.installation.factoryInstall.price)}
                            </p>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    <div className={styles.itemRow}>
                      <p>Accessories shipping cost</p>
                      <p className={styles.strongAmount}>{currency(ACCESSORIES_SHIPPING_COST)}</p>
                    </div>
                    <LinkButton
                      size="sm"
                      underline={false}
                      className={styles.viewAccessoriesLink}
                      onClick={() => {
                        setIsAccessoriesModalOpen(true);
                      }}
                      data-analytics-id="pp_order_summary_view_accessories"
                    >
                      View details
                    </LinkButton>
                  </div>
                </>
              )}
            </div>
          )}

          <div className={styles.breakdownList}>
            {isNewCar && (totalIncentives ?? 0) > 0 && (
              <div className={styles.breakdownList}>
                <div className={styles.itemRow}>
                  <p className={cx(styles.subItem, styles.strongSubItem)}>After discount</p>
                  <p>
                    {currency(
                      (vehicleStickerPrice ?? 0) -
                        (totalDiscounts ?? 0) -
                        (creditKarmaAmountSaved ?? 0) -
                        (tradeBoostAmountSaved ?? 0) -
                        (employeeDiscountAmount ?? 0)
                    )}
                  </p>
                </div>

                <div className={styles.itemRow}>
                  <div
                    className={cx(styles.subItem, styles.expanderHeader)}
                    onClick={() => {
                      setCollapsedIncentives(!collapsedIncentives);
                    }}
                    data-analytics-id="pp_review_order_summary_incentives_toggle"
                    data-testid="incentives-toggle"
                  >
                    <span>Manufacturer incentives</span>
                    <ChevronDown
                      style={{ transform: collapsedIncentives ? undefined : 'rotate(180deg)' }}
                      size={16}
                      color="var(--icon-default)"
                    />
                  </div>
                  <p>{collapsedIncentives ? `${negative}${currency(totalIncentives ?? 0)}` : ''}</p>
                </div>
                {!collapsedIncentives ? (
                  <div className={styles.dropdownContent}>
                    {incentives?.map((item: any) => (
                      <div className={styles.itemRow} key={item.description}>
                        <p>
                          {item.description}
                          {item.expirationDate && (
                            <span className={styles.subtext}>
                              <br />
                              Valid until {dayjs(item.expirationDate).format('MMMM D, YYYY')}
                            </span>
                          )}
                        </p>
                        <p className={styles.strongAmount}>-{currency(item.amount)}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            <div className={cx(styles.itemRow, styles.itemRowStrong)}>
              <p className={styles.subItem}>Vehicle price</p>
              <p>
                {currency(
                  (vehicleStickerPrice ?? 0) -
                    (totalIncentives ?? 0) -
                    (totalDiscounts ?? 0) -
                    (creditKarmaAmountSaved ?? 0) -
                    (tradeBoostAmountSaved ?? 0) -
                    (employeeDiscountAmount ?? 0)
                )}
              </p>
            </div>

            <div className={styles.expandedRow}>
              <div className={styles.itemRow}>
                <p className={styles.subItem}>Sales tax</p>
                <p>{currency(totalTaxes || 0)}</p>
              </div>
              {(tradeInTaxSavings ?? 0) > 0 &&
                !tradeDeclined &&
                completedTrade &&
                isTradeInEligible && (
                  <div className={styles.savingsRow}>
                    You&apos;re saving{' '}
                    {currency(tradeInTaxSavings ?? 0, {
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0
                    })}{' '}
                    in {stateName} sales tax by including your trade-in.
                  </div>
                )}
            </div>

            <div className={styles.itemRow}>
              <div
                className={cx(styles.subItem, styles.expanderHeader)}
                onClick={() => {
                  setCollapsedFees(!collapsedFees);
                }}
                data-analytics-id="pp_review_order_summary_fees_toggle"
              >
                <span>Title and registration</span>
                <ChevronDown
                  style={{ transform: collapsedFees ? undefined : 'rotate(180deg)' }}
                  size={16}
                  color="var(--icon-default)"
                />
              </div>
              <p>{collapsedFees ? currency(totalFees) : ''}</p>
            </div>
            {!collapsedFees ? (
              <div className={styles.dropdownContent}>
                {feesList?.map((item: any) => (
                  <div className={styles.itemRow} key={item.description}>
                    <p>{item.description}</p>
                    <p className={styles.strongAmount}>{currency(item.amount)}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {(shippingFee || 0) > 0 ? (
              <>
                <div className={styles.itemRow}>
                  <div
                    className={cx(styles.subItem, styles.expanderHeader)}
                    onClick={() => {
                      setCollapsedShipping(!collapsedShipping);
                    }}
                    data-analytics-id="pp_review_order_summary_fees_toggle"
                  >
                    <span>Nonrefundable shipping</span>
                    <ChevronDown
                      style={{ transform: collapsedShipping ? undefined : 'rotate(180deg)' }}
                      size={16}
                      color="var(--icon-default)"
                    />
                  </div>
                  <p>{currency(shippingFee ?? 0) || '0'}</p>
                </div>
                {!collapsedShipping && (
                  <div className={styles.dropdownContent}>
                    <p>Transporting the vehicle to a Carvana location near you.</p>
                  </div>
                )}
              </>
            ) : null}

            <div className={styles.dealerFeesRow}>
              <div className={styles.itemRow}>
                <p className={styles.subItem}>Dealer fees</p>
                <p>NEVER</p>
              </div>
              {isInNoDealerFeesExperiment && (
                <div className={styles.dealerFeesSavingsRow}>
                  <span>{dealerFeeSavingsText}</span>{' '}
                  <LinkButton
                    onClick={() => {
                      setIsDealerFeesModalOpen(true);
                    }}
                    className={styles.learnMoreLink}
                    data-analytics-id="pp_no_dealer_fees_learn_more_click"
                    size="xs"
                    underline={false}
                  >
                    Learn more
                  </LinkButton>
                </div>
              )}
            </div>

            {!isInNoDealerFeesExperiment && (
              <div className={styles.itemRow}>
                <p className={styles.subItem}>Hidden fees</p>
                <p>NEVER</p>
              </div>
            )}
          </div>

          <div className={cx(styles.itemRow, styles.itemRowStrong)}>
            <h4>Subtotal</h4>
            <p>{currency(subtotal)}</p>
          </div>
        </div>

        {isEvTaxCreditEligible ? (
          <div
            className={styles.selectionItem}
            data-testid="pp_review-order_price-breakdown_ev-tax-credit"
          >
            <div className={styles.itemRow}>
              <p>Clean Vehicle Tax Credit</p>
              <p>{hasEvTaxCredit ? `-${currency(evTaxCreditAmount || 0)}` : 'Not applied'}</p>
            </div>
            <div className={styles.selectionBox}>
              {hasEvTaxCredit ? (
                <p>
                  You've elected to transfer this tax credit to Carvana and receive a{' '}
                  {currency(evTaxCreditAmount || 0)} total purchase price reduction.
                </p>
              ) : (
                <p>
                  You've elected not to transfer this tax credit to Carvana at time of purchase. You
                  have the option to claim this credit on your next tax return if you meet the
                  eligibility requirements.
                </p>
              )}

              <LinkButton
                size="xs"
                underline={false}
                onClick={() => {
                  navigateToStep(StepName.CleanVehicleTaxCredit);
                }}
                data-analytics-id="pp_order_summary_taxcredit_edit"
                data-testid="taxcredit-button"
              >
                Edit tax credit selection
              </LinkButton>
            </div>
          </div>
        ) : null}

        {hasSelections && (
          <div
            className={styles.selectionsSection}
            data-testid="pp_review-order_price-breakdown_trade-in"
          >
            {isTradeInEligible && completedTrade && (
              <div className={styles.selectionItem}>
                <div className={styles.itemRow} data-testid="trade-row">
                  <p>{tradeBreakdownTitle}</p>
                  <p>
                    {tradeDeclined
                      ? 'Declined'
                      : `${calculatedTradeValue > 0 ? '-' : ''}${currency(calculatedTradeValue)}`}
                  </p>
                </div>
                <div className={styles.selectionBox}>
                  <div
                    className={styles.tradeHtml}
                    dangerouslySetInnerHTML={{ __html: tradeMessage }}
                  />
                  <LinkButton
                    size="xs"
                    underline={false}
                    onClick={() => {
                      if (tradeDeclined) {
                        navigateToStep(StepName.TradeIn);
                      } else {
                        setIsTradeInModalOpen(true);
                      }
                    }}
                    data-analytics-id="pp_review_order_summary_edit_tradein"
                  >
                    {tradeDeclined ? 'Add trade-in' : 'View details'}
                  </LinkButton>
                </div>
              </div>
            )}

            {hasTransferMethod && transferAddress && (
              <div
                className={styles.selectionItem}
                data-testid="pp_review-order_price-breakdown_transfer-method"
              >
                <div className={styles.itemRow} data-testid="delivery-row">
                  <p>{isPickup ? 'Pickup' : 'Delivery'}</p>
                  <p>{isDelivery && deliveryCharge ? currency(deliveryCharge) : 'FREE'}</p>
                </div>
                <div className={styles.selectionBox}>
                  <p className={styles.addressHeading}>
                    {isPickup ? 'Picking up at' : 'Home delivery to'}
                  </p>
                  <div className={styles.addressText}>
                    {isVendingMachine && isPickup && <p>Carvana Vending Machine</p>}
                    <p>
                      {transferAddress.line1} {transferAddress.line2 && transferAddress.line2}
                    </p>
                    <p>
                      {transferAddress.city}, {transferAddress.state} {transferAddress.zipCode}
                    </p>
                  </div>
                  <LinkButton
                    size="xs"
                    underline={false}
                    onClick={() => {
                      navigateToStep(StepName.PickupDelivery);
                    }}
                    data-analytics-id="pp_review_order_breakdown_edit_pickup_delivery"
                  >
                    Edit {isPickup ? 'pickup' : 'delivery'}
                  </LinkButton>
                </div>
              </div>
            )}

            {productStatus !== null && productStatus !== 'NotSet' ? (
              <div
                className={styles.selectionItem}
                data-testid="pp_review-order_price-breakdown_carvana-care"
              >
                <div className={styles.itemRow} data-testid="carvanacare-row">
                  <p>
                    CarvanaCare<sup>®</sup>
                    {isInTreatment && !isExperimentLoading && productType ? ` ${productType}` : ''}
                  </p>
                  <p>{productStatus === 'Declined' ? 'Declined' : currency(productPrice ?? 0)}</p>
                </div>
                <div className={styles.selectionBox}>
                  {productStatus === 'Applied' ? (
                    <>
                      {isInTreatment && !isExperimentLoading && productMonths ? (
                        <p>
                          You&apos;re protected for{' '}
                          <strong>
                            {productMonths} months
                            {productMileage ? ` / ${productMileage.toLocaleString()} miles` : ''}
                          </strong>
                          .
                        </p>
                      ) : (
                        <p>You added {productType} coverage.</p>
                      )}
                      <LinkButton
                        size="xs"
                        underline={false}
                        onClick={() => {
                          trackEvent('review order vsc clicked');
                          setIsCarvanaCareModalOpen(true);
                        }}
                        data-analytics-id={'pp_review_order_summary_view_carvanacare_details'}
                      >
                        View details
                      </LinkButton>
                    </>
                  ) : (
                    <>
                      <p>
                        You declined CarvanaCare<sup>®</sup> extended protection.
                      </p>
                      <LinkButton
                        size="xs"
                        underline={false}
                        onClick={() => {
                          trackEvent('review order vsc clicked');
                          navigateToStep(StepName.VSC);
                        }}
                        data-analytics-id="pp_review_order_summary_add_carvanacare"
                      >
                        Add CarvanaCare
                      </LinkButton>
                    </>
                  )}
                </div>
              </div>
            ) : null}

            {gapStatus !== null && gapStatus !== 'NotSet' ? (
              <div
                className={styles.selectionItem}
                data-testid="pp_review-order_price-breakdown_gap-coverage"
              >
                <div className={styles.itemRow} data-testid="gap-row">
                  <p>GAP coverage</p>
                  <p>{gapStatus === 'Declined' ? 'Declined' : currency(gapPrice || 0)}</p>
                </div>
                <div className={styles.selectionBox}>
                  {isInTreatment &&
                  !isExperimentLoading &&
                  gapStatus === 'Applied' &&
                  gapModalProps.termMonths ? (
                    <p>
                      You&apos;re protected for the{' '}
                      <strong>full length of your loan of {gapModalProps.termMonths} months</strong>{' '}
                      or until your loan is paid off.
                    </p>
                  ) : (
                    <p>You {gapStatus === 'Declined' ? 'declined' : 'added'} GAP coverage.</p>
                  )}

                  {isInTreatment && !isExperimentLoading && gapStatus === 'Applied' ? (
                    <LinkButton
                      size="xs"
                      underline={false}
                      onClick={() => {
                        setIsGapModalOpen(true);
                      }}
                      data-analytics-id="pp_review_order_summary_view_gap_details"
                    >
                      View details
                    </LinkButton>
                  ) : (
                    <LinkButton
                      size="xs"
                      underline={false}
                      onClick={() => {
                        navigateToStep(StepName.Gap);
                      }}
                      data-analytics-id="pp_review_order_summary_edit_protection"
                    >
                      {gapStatus === 'Declined' ? 'Add' : 'Edit'} GAP coverage
                    </LinkButton>
                  )}
                </div>
              </div>
            ) : null}

            {mayShowVinEtching && showVinEtching ? (
              <div
                className={styles.selectionItem}
                data-testid="pp_review-order_price-breakdown_vin-etching"
              >
                <div className={styles.itemRow} data-testid="vin-etching-row">
                  <p>VIN marking service</p>
                  <p>{isVinEtchingApplied ? 'Applied' : 'Declined'}</p>
                </div>
                <div className={styles.selectionBox}>
                  <p>
                    We'll mark your vehicle's VIN on its catalytic converter. This service will
                    delay the earliest available pickup/delivery appointment by six weeks.
                  </p>
                  {!isVinEtchingApplied && (
                    <p>By continuing with your order, you are declining this service.</p>
                  )}

                  <LinkButton
                    size="xs"
                    underline={false}
                    onClick={() => {
                      setIsVinEtchingModalOpen(true);
                    }}
                    data-analytics-id={
                      isVinEtchingApplied ? 'pp_vin_edit_selection' : 'pp_vin_enroll_in_service'
                    }
                    data-testid="vin-etching-button"
                  >
                    {isVinEtchingApplied ? 'Edit selection' : 'Enroll in service'}
                  </LinkButton>
                </div>
              </div>
            ) : null}

            <div className={styles.divider} />

            <div className={cx(styles.itemRow, styles.totalSection)}>
              <p>Total purchase price</p>
              <p>{currency(totalPrice || 0)}</p>
            </div>
          </div>
        )}
      </div>
      <GapCoverageModal
        isOpen={isGapModalOpen}
        onClose={() => {
          setIsGapModalOpen(false);
        }}
        impactsFinancingTerms={gapModalProps.impactsFinancingTerms}
        vehicleModel={gapModalProps.vehicleModel}
        termMonths={gapModalProps.termMonths}
        monthlyDelta={gapModalProps.monthlyDelta}
        downDelta={gapModalProps.downDelta}
        termDelta={gapModalProps.termDelta}
        fullPrice={gapPrice ?? 0}
        isFinanced={isFinanced}
        experimentName={experimentName}
        experimentArm={experimentArm}
      />
      {showAccessories && (
        <SlateAccessoriesModal
          accessories={accessories}
          totalValue={accessoriesTotal}
          vehicle={
            summary?.vehicle
              ? {
                  year: summary.vehicle.year,
                  model: summary.vehicle.model,
                  imageUrl: summary.vehicle.primaryImageUrl ?? summary.vehicle.fallbackImageUrl
                }
              : null
          }
          isOpen={isAccessoriesModalOpen}
          onClose={() => {
            setIsAccessoriesModalOpen(false);
          }}
        />
      )}
    </section>
  );
};

export default PriceBreakdown;
